import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { X } from 'lucide-react';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  /** Ações à direita do título (antes do X). */
  headerActions?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  size?: 'md' | 'lg' | 'xl';
  /** Esc fecha (desligue quando um modal por cima já trata o Esc). */
  closeOnEsc?: boolean;
}

const SIZES = { md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

/** Painel lateral à direita (slide-over), em portal como o Modal do Dialog.tsx. Esc fecha. */
export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  headerActions,
  footer,
  children,
  size = 'lg',
  closeOnEsc = true,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEsc) onClose();
    };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, closeOnEsc]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black bg-opacity-40 z-40 animate-fade-in" onClick={onClose} />
      <aside
        role="dialog"
        aria-modal="true"
        className={clsx(
          'fixed inset-y-0 right-0 z-50 w-full bg-white shadow-xl flex flex-col animate-fade-in',
          SIZES[size],
        )}
      >
        <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-200">
          <div className="min-w-0 flex-1 text-h4 truncate">{title}</div>
          <div className="flex items-center gap-2">
            {headerActions}
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              className="p-1.5 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100"
            >
              <X size={20} />
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-4">{children}</div>
        {footer && <footer className="px-5 py-3 border-t border-slate-200 bg-slate-50">{footer}</footer>}
      </aside>
    </>,
    document.body,
  );
};

export default Drawer;
